import debug from '../console'
import {
  consentCountrySource,
  consentDecisionOrigin,
  consentSnapshot,
  consentUsDefaultAllow,
} from './consentGate'
import { CONSENT_TIMING_PREFIX, markConsentTiming } from './consentTiming'

const logger = debug.withTag('utils:consent')

/** The ad providers that count toward `first_ad_visible`. */
export type AdRequestProvider = 'display' | 'taboola' | 'outbrain' | 'primis'

const FIRST_AD_REQUEST_MARK = 'first-ad-request'

/** The start of the mark `markFirstAdRequest` leaves, before `<provider>:<state>`. */
export const FIRST_AD_REQUEST_PREFIX = `${CONSENT_TIMING_PREFIX}${FIRST_AD_REQUEST_MARK}:`

const NONE = 'none'

/**
 * The consent decision in effect at the page load's first ad request, as the
 * small fixed strings RUM tags it with. Taken once, at the request, and never
 * read again, so a later revision of the decision does not change it.
 */
export interface FirstAdRequestSnapshot {
  provider: AdRequestProvider
  state: string
  reason: string
  policy: string
  nonPersonalizedAds: string
  restrictDataProcessing: string
  identity: string
  firstPartyId: string
  /** `package`, `app_fallback` (the gate's own unknown decision), or `none`. */
  origin: string
  /** `filters`, `fallback` (no country), or `none`. */
  countrySource: string
}

let marked = false
let snapshot: FirstAdRequestSnapshot | null = null

function takeSnapshot(provider: AdRequestProvider): FirstAdRequestSnapshot {
  const decision = consentSnapshot()
  return {
    provider,
    state: decision?.state ?? NONE,
    reason: decision?.reason ?? NONE,
    policy: decision?.policy ?? NONE,
    nonPersonalizedAds: decision
      ? String(decision.restrictions.nonPersonalizedAds)
      : NONE,
    restrictDataProcessing: decision
      ? String(decision.restrictions.restrictDataProcessing)
      : NONE,
    identity: decision?.identity ?? NONE,
    firstPartyId: decision?.firstPartyId ?? NONE,
    origin: consentDecisionOrigin(),
    countrySource: consentCountrySource(),
  }
}

/**
 * Records the page load's first ad request, from whichever provider asks
 * first, with the consent decision in effect at that moment:
 * `consent-bootstrap:first-ad-request:<provider>:<state>`. The state is read
 * live, not rebuilt from the decision marks, because those mark each state
 * only once and miss a return to an earlier one. The rest of the decision is
 * kept as a snapshot, see `firstAdRequestSnapshot`, and the mark carries the
 * snapshot's state, so both read the decision once.
 *
 * Only a measurement: anything that fails here loses the measurement and is
 * never thrown into the ad request that calls this.
 */
export function markFirstAdRequest(provider: AdRequestProvider): void {
  if (marked) return
  marked = true
  try {
    snapshot = takeSnapshot(provider)
    markConsentTiming(`${FIRST_AD_REQUEST_MARK}:${provider}:${snapshot.state}`)
  } catch (error) {
    logger.warn('Could not record the first ad request', error)
  }
}

/** The snapshot taken at the page load's first ad request, or null before it. */
export function firstAdRequestSnapshot(): FirstAdRequestSnapshot | null {
  return snapshot
}

/**
 * The `consent_*` RUM tags for a snapshot, every one `none` without it, and
 * `us_default_allow`, the arm consent started with, which needs no snapshot.
 * The state and the provider have their own tags, `consent_decision` and
 * `first_ad_request`.
 */
export function consentSnapshotContext(
  taken: FirstAdRequestSnapshot | null
): Record<string, string> {
  return {
    consent_reason: taken?.reason ?? NONE,
    consent_policy: taken?.policy ?? NONE,
    consent_npa: taken?.nonPersonalizedAds ?? NONE,
    consent_rdp: taken?.restrictDataProcessing ?? NONE,
    consent_identity: taken?.identity ?? NONE,
    consent_first_party_id: taken?.firstPartyId ?? NONE,
    consent_origin: taken?.origin ?? NONE,
    consent_country_source: taken?.countrySource ?? NONE,
    us_default_allow: consentUsDefaultAllow(),
  }
}

/**
 * The tags the ad vitals carry: the first ad request's state and provider,
 * then `consentSnapshotContext`. Empty when they cannot be read, so the vital
 * still goes out.
 */
export function firstAdRequestContext(
  read: () => FirstAdRequestSnapshot | null = firstAdRequestSnapshot
): Record<string, string> {
  try {
    const taken = read()
    return {
      consent_decision: taken?.state ?? NONE,
      first_ad_request: taken?.provider ?? NONE,
      ...consentSnapshotContext(taken),
    }
  } catch {
    return {}
  }
}

export function resetFirstAdRequestForTests(): void {
  marked = false
  snapshot = null
}
